import { AxiosResponse } from "axios";
import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";
import * as actions from "./actions";
import * as actionTypes from "./actionTypes";
import * as service from "./service";
import * as types from "./types";
import * as alert from "../alert/actions";
//Override List
function* fetchOverrideListSaga(action: types.FetchOverridesReq) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IOverrideList | types.IApiError> = yield call(service.getOverrides, action.params);

        if (response.status === 200) {
            yield put(
                actions.fetchOverridesSuccess({
                    overrides: response.data as types.IOverrideList,
                })
            );
        }

        //Show IApiError
        if (response.status !== 200) {
            let apiError = response.data as types.IApiError;
            let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}. Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errmsg, options: { variant: "error" } }));
        }

    } catch (e: any) {
        yield put(
            actions.fetchOverridesFailure({
                error: e,
            })
        );
    }
}

function* fetchOverrideDetailsSaga(action: types.FetchOverrideReq) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IOverrideDetails | types.IApiError> = yield call(service.getOverrideDetails, action.params);

        if (response.status === 200) {
            yield put(
                actions.fetchOverrideDetailsSuccess({
                    override: response.data as types.IOverrideDetails,
                })
            );
        }

        //Show IApiError
        if (response.status !== 200) {
            let apiError = response.data as types.IApiError;
            let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}. Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errmsg, options: { variant: "error" } }));
            yield put(
                actions.fetchOverrideDetailsFailure({
                    error: response.data as types.IApiError,
                })
            );
        }

    } catch (e: any) {
        yield put(
            actions.fetchOverrideDetailsFailure({
                error: e,
            })
        );
    }
}

function* deleteOverrideSaga(action: types.DeleteOverrideRequest) {
    const timestamp = new Date().toLocaleString();

    try {
        const response: AxiosResponse<types.IDeleteOverrideSuccess | types.IApiError> = yield call(service.deleteOverride, action.params);

        if (response.status === 204) {
            yield put(
                actions.deleteOverrideSuccess({
                    success: true
                })
            );
            yield put(alert.enqueueSnackbar({ message: `The Override was successfully deleted. Timestamp: ${timestamp}`, options: { variant: "success" } }));

        }

        //Show IApiError
        if (response.status !== 204) {
            let apiError = response.data as types.IApiError;
            let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}. Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errmsg, options: { variant: "error" } }));
        }

    } catch (e: any) {
        yield put(
            actions.deleteOverrideFailure({
                error: e,
            })
        );
    }
}

function* postOverrideSaga({ payload }: types.PostOverrideRequest) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IPostOverrideSuccess[] | types.IApiError> = yield call(service.postOverride, payload);

        if (response.status === 201) {
            let dataPayload = response.data as types.IPostOverrideSuccess[];
            yield put(
                actions.postOverrideSuccess({
                    override: dataPayload,
                })
            );
            let overrideIds = dataPayload.map((data) => data.overrideId).join(',');
            let successMsg = dataPayload.length > 1 ? `The Override were successfully created, with Override IDs: ${overrideIds}. Timestamp: ${timestamp}` : `The Override was successfully created, with Override ID: ${overrideIds}. Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: successMsg, options: { variant: "success" } }));
        }

        if (response.status !== 201) {
            let apiError = response.data as types.IApiError;
            let errMsg = `StatusCode: ${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}. Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errMsg, options: { variant: "error" } }));
        }
    } catch (e: any) {
        yield put(
            actions.postOverrideFailure({
                error: e,
            })
        );
    }
}

function* putOverrideSaga({ overrideId, payload }: types.PutOverrideRequest) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IPutOverrideSuccess | types.IApiError> = yield call(service.putOverride, overrideId, payload);

        if (response.status === 204) {
            let dataPayload = response.data as types.IPutOverrideSuccess;
            yield put(
                actions.putOverrideSuccess({
                    override: dataPayload,
                })
            );
            let successMsg = `The Override was successfully edited. override ID: ${overrideId}. Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: successMsg, options: { variant: "success" } }));
        }

        if (response.status !== 204) {
            let apiError = response.data as types.IApiError;
            let errMsg = `StatusCode: ${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}. Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errMsg, options: { variant: "error" } }));
        }
    } catch (e: any) {
        yield put(
            actions.putOverrideFailure({
                error: e,
            })
        );
    }
}

/*
  Watcher saga
*/

function* overrideListSaga() {
    yield takeLatest(actionTypes.FETCH_OVERRIDES_LIST_REQUEST, fetchOverrideListSaga);
}

function* overrideDetailsSaga() {
    yield takeEvery(actionTypes.FETCH_OVERRIDE_REQUEST, fetchOverrideDetailsSaga);
}

function* removeOverrideSaga() {
    yield takeEvery(actionTypes.DELETE_OVERRIDE_REQUEST, deleteOverrideSaga);
}

function* createOverrideSaga() {
    yield takeLatest(actionTypes.POST_OVERRIDE_REQUEST, postOverrideSaga);
}

function* editOverrideSaga() {
    yield takeEvery(actionTypes.PUT_OVERRIDE_REQUEST, putOverrideSaga);
}


function* overrideSaga() {
    yield all([
        overrideListSaga(),
        overrideDetailsSaga(),
        createOverrideSaga(),
        removeOverrideSaga(),
        editOverrideSaga(),
    ])
}

export default overrideSaga;